import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { useModalA11y } from '../lib/useModalA11y';

/**
 * Shared primitives for the canonical Citadel detail/edit modal pattern
 * — adopted from `latest/prototypes/2026-05-21/client-detail-mockups.html`
 * Variant B (cover-band header). Every detail or edit modal in CipherOps
 * uses these so the visual rhythm stays uniform.
 *
 * Decision 2026-05-21: this pattern supersedes the two-column dossier
 * (Variant 4) used briefly on Client Detail. The cover-band reads as
 * "the entity's mast-head" — title + crimson rule + italic subtitle +
 * optional band stats — and pairs with single-column section-headed
 * body content.
 *
 * Usage:
 *   <ModalCoverBand
 *     title="Sale · 2026-001"
 *     subtitle="Cyberdyne Systems · ENG-2026-001-CYB"
 *     stats={[
 *       { label: 'Value', value: '£10,000' },
 *       { label: 'Days',  value: '10' },
 *     ]}
 *     onClose={onClose}
 *   />
 *   ... body content using <ModalSectionHead label="Status" /> ...
 */

interface BandStat {
  label: string;
  value: ReactNode;
  /** Use for highlighting an outstanding balance, etc. */
  tone?: 'default' | 'warn';
}

export function ModalCoverBand({
  title,
  subtitle,
  stats,
  chip,
  onClose,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  stats?: BandStat[];
  /** Small mono badge pinned to the top-right of the band (e.g. short_name). */
  chip?: ReactNode;
  onClose: () => void;
}) {
  // A11y (2026-06-30): make the surrounding modal an accessible dialog.
  // ModalCoverBand renders *inside* the `.modal` element, so we resolve that
  // ancestor once on mount and hand it to useModalA11y. Every modal that uses
  // the cover band inherits dialog semantics + focus trap + Escape + restore
  // with no call-site changes. The title <h2> provides the accessible name via
  // aria-labelledby.
  const closeBtnRef = useRef<HTMLButtonElement | null>(null);
  const titleId = useId();
  const [dialogEl, setDialogEl] = useState<HTMLElement | null>(null);

  useEffect(() => {
    const ancestor = closeBtnRef.current?.closest<HTMLElement>('.modal') ?? null;
    setDialogEl(ancestor);
  }, []);

  useModalA11y(dialogEl, onClose, { labelledBy: titleId });

  return (
    <>
      <button
        ref={closeBtnRef}
        className="modal-close"
        onClick={onClose}
        style={{
          position: 'absolute',
          top: 12,
          right: 14,
          background: 'transparent',
          border: 'none',
          fontSize: 22,
          lineHeight: 1,
          cursor: 'pointer',
          color: 'rgba(255,255,255,0.85)',
          zIndex: 2,
        }}
        aria-label="Close"
      >×</button>
      <div
        style={{
          background: 'var(--brand, #1a1f2e)',
          color: '#fff',
          padding: '20px 24px 16px',
          position: 'relative',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
          <h2 id={titleId} style={{
            fontFamily: 'var(--serif)',
            fontWeight: 'normal',
            margin: 0,
            fontSize: 20,
            flex: 1,
            paddingRight: 28, // give the × room
          }}>
            {title}
          </h2>
          {chip && (
            <div style={{
              fontFamily: 'var(--mono)',
              fontSize: 10,
              letterSpacing: '1.5px',
              color: 'rgba(255,255,255,0.6)',
              border: '0.5px solid rgba(255,255,255,0.3)',
              padding: '3px 8px',
              marginTop: 4,
              marginRight: 30, // clear the × close button
            }}>
              {chip}
            </div>
          )}
        </div>
        <div style={{
          width: 36,
          height: '1.5px',
          background: 'var(--accent, #8b1a2e)',
          margin: '8px 0',
        }} />
        {subtitle && (
          <div style={{
            fontFamily: 'var(--serif)',
            fontStyle: 'italic',
            fontSize: 12,
            color: 'rgba(255,255,255,0.72)',
          }}>
            {subtitle}
          </div>
        )}
        {stats && stats.length > 0 && (
          <div style={{ display: 'flex', gap: 28, marginTop: 10, flexWrap: 'wrap' }}>
            {stats.map((s, i) => (
              <div
                key={i}
                style={{
                  color: 'rgba(255,255,255,0.85)',
                  fontFamily: 'var(--mono)',
                  fontSize: 11,
                  letterSpacing: '1px',
                }}
              >
                {s.label}
                <strong
                  style={{
                    fontFamily: 'var(--serif)',
                    fontSize: 16,
                    fontWeight: 'normal',
                    color: s.tone === 'warn' ? '#ffb0a0' : '#fff',
                    display: 'block',
                    marginTop: 2,
                  }}
                >
                  {s.value}
                </strong>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

/**
 * Mono uppercase letter-spaced label with a horizontal rule above —
 * marks the start of each section in a Variant B modal body.
 *
 * `badge` is rendered to the right of the label (e.g. a count chip).
 *
 * `tone="first"` removes the top border so the first section sits flush
 * against the cover band without a phantom line.
 */
export function ModalSectionHead({
  label,
  badge,
  first,
}: {
  label: ReactNode;
  badge?: ReactNode;
  first?: boolean;
}) {
  return (
    <div
      style={{
        borderTop: first ? 'none' : '0.5px solid var(--border)',
        marginTop: first ? 0 : 18,
        paddingTop: first ? 0 : 12,
        marginBottom: 12,
        display: 'flex',
        alignItems: 'baseline',
        justifyContent: 'space-between',
        gap: 10,
      }}
    >
      <div style={{
        fontFamily: 'var(--mono)',
        fontSize: 10.5,
        letterSpacing: '3px',
        textTransform: 'uppercase',
        color: 'var(--text-muted)',
      }}>
        {label}
      </div>
      {badge}
    </div>
  );
}
